import React from 'react';
import { Box, Typography, Button, Container, Grid, Card, CardContent } from '@mui/material';
import { motion } from 'framer-motion';
import { CheckCircle, ExternalLink, QrCode, Sparkles } from 'lucide-react';

const DEMO_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSdUXBmeB096Bx_JreweZ9rSUG7K7HmtLqRgWTvPjrv3-RybBQ/viewform?usp=dialog';
const QR_IMAGE_URL = '/form_qrcode.png';

const PACKAGE_STEPS = [
  'Structured Training in AI, Data, and LLM foundations',
  'Hands-on real industry projects (live project workflow)',
  'Resume preparation + portfolio storytelling',
  'Profile marketing and interview scheduling support',
  'Interview preparation with guidance and mock rounds',
  'Post-placement technical support when you need help',
];

const WHO_IS_THIS_FOR = [
  'Students who want to start an AI + Data career',
  'Working professionals planning a career switch',
  'Job seekers who need practical project experience',
  'Developers moving into AI + Data Engineering with LLMs',
];

const ROLE_OUTCOMES = [
  'AI Engineer (Entry to Mid Level)',
  'Data Engineer (with AI workflow skills)',
  'LLM Application Developer',
  'AI Automation Specialist',
];

const SKILL_CATEGORIES = [
  {
    title: 'AI + LLM Engineering',
    skills: [
      { name: 'Python', logo: 'https://cdn.simpleicons.org/python/3776AB' },
      { name: 'LangChain', logo: 'https://cdn.simpleicons.org/langchain/1C3C3C' },
      { name: 'LangGraph', shortLabel: 'LG' },
      { name: 'Vector Databases', shortLabel: 'VDB' },
      { name: 'FAISS', shortLabel: 'FS' },
      { name: 'Pinecone', shortLabel: 'PC' },
    ],
  },
  {
    title: 'Data Engineering + Analytics',
    skills: [
      { name: 'PostgreSQL', logo: 'https://cdn.simpleicons.org/postgresql/336791' },
      { name: 'MySQL', logo: 'https://cdn.simpleicons.org/mysql/4479A1' },
      { name: 'Snowflake', logo: 'https://cdn.simpleicons.org/snowflake/29B5E8' },
      { name: 'PySpark', logo: 'https://cdn.simpleicons.org/apachespark/E25A1C' },
      { name: 'dbt', logo: 'https://cdn.simpleicons.org/dbt/FF694B' },
      { name: 'Databricks', logo: 'https://cdn.simpleicons.org/databricks/FF3621' },
      { name: 'Power BI', shortLabel: 'PBI' },
      { name: 'Pipeline Design', shortLabel: 'ETL' },
    ],
  },
  {
    title: 'Professional Tools + Deployment',
    skills: [
      { name: 'Git', logo: 'https://cdn.simpleicons.org/git/F05032' },
      { name: 'Linux', logo: 'https://cdn.simpleicons.org/linux/FCC624' },
      { name: 'Jira', logo: 'https://cdn.simpleicons.org/jira/0052CC' },
      { name: 'AWS', shortLabel: 'AWS' },
      { name: 'Docker', logo: 'https://cdn.simpleicons.org/docker/2496ED' },
    ],
  },
];

const AIDataCoursePage: React.FC = () => {
  return (
    <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh', pt: { xs: 8, md: 9 }, pb: { xs: 7, md: 10 } }}>
      <Container maxWidth="lg">
        <Grid container spacing={4} sx={{ alignItems: 'center', mb: { xs: 5, md: 8 } }}>
          <Grid size={{ xs: 12, md: 7 }}>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: '#DBEAFE', color: '#1D4ED8', px: 2, py: 0.8, borderRadius: '999px', fontWeight: 700, fontSize: '0.88rem', mb: 2 }}>
                <Sparkles size={16} />
                New Career Track - 2026
              </Box>

              <Typography component="h1" sx={{ fontSize: { xs: '1.9rem', md: '2.6rem' }, lineHeight: 1.24, fontWeight: 800, color: '#0F172A', mb: 2 }}>
                Switch to an AI &amp; Data Career in 2026
              </Typography>

              <Typography sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, color: '#1E3A8A', fontWeight: 700, mb: 2.2 }}>
                Upgrade your career with AI, Data, and LLMs — this is not just training, it is a complete job-ready package.
              </Typography>

              <Typography sx={{ color: '#475569', lineHeight: 1.75, mb: 3 }}>
                If you are a student, working professional, or currently not working but interested in AI, this program helps you build real-world skills and confidently move into AI + Data roles.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1, mb: 3.2 }}>
                {[
                  'Training first, then real project work in AI',
                  'Understand how real industry teams work',
                  'Career support from learning to placement',
                  'Interview prep and post-join technical support',
                ].map((item) => (
                  <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1.3 }}>
                    <CheckCircle size={18} color="#16A34A" />
                    <Typography sx={{ color: '#1E293B', fontWeight: 500 }}>{item}</Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                <Button
                  variant="contained"
                  href={DEMO_FORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<ExternalLink size={18} />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '1rem',
                    borderRadius: '12px',
                    px: 3.2,
                    py: 1.3,
                    background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                    '&:hover': { background: 'linear-gradient(135deg, #1D4ED8 0%, #1E3A8A 100%)' },
                  }}
                >
                  Book Your Free Demo
                </Button>
              </Box>
            </motion.div>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <motion.div initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.65 }}>
              <Card sx={{ borderRadius: '20px', overflow: 'hidden', border: '1px solid #DBEAFE', boxShadow: '0 16px 36px rgba(37, 99, 235, 0.16)' }}>
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1200&q=80"
                  alt="AI and data professionals collaborating"
                  sx={{ width: '100%', height: { xs: 220, md: 310 }, objectFit: 'cover' }}
                />
                <CardContent sx={{ p: 2.3 }}>
                  <Typography sx={{ color: '#0F172A', fontWeight: 700, mb: 0.7 }}>For Developers & Professionals</Typography>
                  <Typography sx={{ color: '#64748B', fontSize: '0.95rem' }}>
                    Move into AI + Data Engineering using modern Large Language Model workflows.
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: { xs: 5, md: 7 } }}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Card sx={{ borderRadius: '18px', border: '1px solid #BFDBFE', height: '100%', boxShadow: '0 12px 28px rgba(29, 78, 216, 0.10)' }}>
              <CardContent sx={{ p: { xs: 2.3, md: 3 } }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', px: 1.5, py: 0.55, borderRadius: '999px', mb: 1.5, fontWeight: 700, fontSize: '0.82rem' }}>
                  <Sparkles size={14} /> Complete Package
                </Box>
                <Typography sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' }, fontWeight: 800, color: '#0F172A', mb: 1.1 }}>
                  Simple, Focused Offering
                </Typography>
                <Typography sx={{ color: '#334155', mb: 2, lineHeight: 1.7 }}>
                  We are not offering just training. We offer a complete package from training to placement support.
                </Typography>
                <Box sx={{ display: 'grid', gap: 1.1, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                  {PACKAGE_STEPS.map((step, index) => (
                    <Box key={step} sx={{ display: 'flex', gap: 1.1, alignItems: 'flex-start', p: 1.2, borderRadius: '12px', bgcolor: '#F8FAFF', border: '1px solid #DBEAFE' }}>
                      <Box sx={{ minWidth: 24, height: 24, borderRadius: '999px', bgcolor: '#DBEAFE', color: '#1D4ED8', fontWeight: 800, fontSize: '0.76rem', display: 'grid', placeItems: 'center', mt: '2px' }}>
                        {index + 1}
                      </Box>
                      <Box>
                        <Typography sx={{ color: '#1E293B', fontWeight: 600, lineHeight: 1.55 }}>{step}</Typography>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, color: '#16A34A', mt: 0.6, fontSize: '0.78rem', fontWeight: 600 }}>
                          <CheckCircle size={14} /> Included
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Card sx={{ borderRadius: '16px', border: '1px solid #E2E8F0', height: '100%' }}>
              <CardContent sx={{ p: { xs: 2.3, md: 3 } }}>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: '#0F172A', mb: 1.3 }}>Who Should Join</Typography>
                <Box sx={{ display: 'grid', gap: 1 }}>
                  {WHO_IS_THIS_FOR.map((item) => (
                    <Typography key={item} sx={{ color: '#475569' }}>• {item}</Typography>
                  ))}
                </Box>

                <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#0F172A', mt: 2.2, mb: 1 }}>Career Outcomes</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {ROLE_OUTCOMES.map((role) => (
                    <Box key={role} sx={{ px: 1.2, py: 0.7, borderRadius: '999px', fontSize: '0.84rem', fontWeight: 600, bgcolor: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>
                      {role}
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card sx={{ borderRadius: '18px', border: '1px solid #C7D2FE', bgcolor: '#F8FAFF', mb: { xs: 5, md: 7 } }}>
          <CardContent sx={{ p: { xs: 2.3, md: 3 } }}>
            <Typography sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 800, color: '#0F172A', mb: 0.9 }}>
              Skills Included in Learning
            </Typography>
            <Typography sx={{ color: '#475569', mb: 2.3 }}>
              Complete stack for AI + Data careers: programming, LLM tools, vector databases, ETL workflows, cloud, and professional engineering tools.
            </Typography>

            <Box sx={{ display: 'grid', gap: 2.2 }}>
              {SKILL_CATEGORIES.map((category) => (
                <Box key={category.title}>
                  <Typography sx={{ fontWeight: 700, color: '#1E3A8A', mb: 1.1 }}>{category.title}</Typography>
                  <Grid container spacing={1.2}>
                    {category.skills.map((skill) => (
                      <Grid key={skill.name} size={{ xs: 6, sm: 4, md: 3 }}>
                        <Box
                          sx={{
                            height: '100%',
                            border: '1px solid #DBEAFE',
                            borderRadius: '12px',
                            bgcolor: '#FFFFFF',
                            px: 1.1,
                            py: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.9,
                          }}
                        >
                          {skill.logo ? (
                            <Box
                              component="img"
                              src={skill.logo}
                              alt={`${skill.name} logo`}
                              sx={{ width: 18, height: 18, objectFit: 'contain', flexShrink: 0 }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '6px',
                                bgcolor: '#DBEAFE',
                                color: '#1D4ED8',
                                fontSize: '0.62rem',
                                fontWeight: 800,
                                display: 'grid',
                                placeItems: 'center',
                                flexShrink: 0,
                              }}
                            >
                              {skill.shortLabel}
                            </Box>
                          )}
                          <Typography sx={{ color: '#1E293B', fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.3 }}>
                            {skill.name}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: '18px', border: '1px solid #BFDBFE', bgcolor: '#EFF6FF' }}>
          <CardContent sx={{ p: { xs: 2.5, md: 3.2 } }}>
            <Grid container spacing={3} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography sx={{ fontSize: { xs: '1.35rem', md: '1.8rem' }, fontWeight: 800, color: '#0F172A', mb: 1.1 }}>
                  Book Your Free Demo Now
                </Typography>
                <Typography sx={{ color: '#334155', mb: 2 }}>
                  Scan the QR code or click the button to reserve your free demo session and get your AI &amp; Data career roadmap.
                </Typography>
                <Button
                  variant="contained"
                  href={DEMO_FORM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<ExternalLink size={18} />}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', px: 3, py: 1.2, background: '#1D4ED8', '&:hover': { background: '#1E3A8A' } }}
                >
                  Book Free Demo
                </Button>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ display: 'grid', placeItems: { xs: 'start', md: 'center' } }}>
                  <Box sx={{ bgcolor: '#fff', borderRadius: '14px', p: 1.2, border: '1px solid #DBEAFE', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)' }}>
                    <Box component="img" src={QR_IMAGE_URL} alt="Scan QR code to book free demo" sx={{ width: { xs: 170, md: 210 }, height: { xs: 170, md: 210 }, display: 'block' }} />
                  </Box>
                  <Typography sx={{ mt: 1, color: '#475569', fontSize: '0.86rem', display: 'flex', alignItems: 'center', gap: 0.7 }}>
                    <QrCode size={15} /> Scan QR to book demo
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default AIDataCoursePage;
